import { html } from '../lib/html.js';
import { icon } from '../lib/icons.js';
import { getProgress } from '../lib/store.js';
import { getIndex, chapterStats, overallStats, nextStop, chapterSky } from '../lib/content.js';
import { percent, roman, plural } from '../lib/format.js';
import { topbar } from '../components/chrome.js';
import { ring } from '../components/ring.js';

function status(s, isNext) {
  if (s.complete) return html`<span class="badge badge--accent">${icon('check', { size: 14, stroke: 2.5 })} Done</span>`;
  if (s.done > 0) return html`<span class="badge badge--accent">${s.done} of ${s.total}</span>`;
  if (isNext) return html`<span class="badge badge--accent">Up next</span>`;
  return '';
}

function stop(ch, meta, progress, nextCh) {
  const s = chapterStats(progress, ch);
  const sky = chapterSky(ch);
  const isNext = nextCh === ch;
  const href = `/chapter/${ch}`;
  return html`<li class="stop${s.complete ? ' is-complete' : ''}${isNext ? ' is-next' : ''}" style="--sky:${sky.top};--sky-2:${sky.bottom};--sky-ink:${sky.ink}">
    <a class="stop__link" href="${href}">
      <span class="stop__node" aria-hidden="true">
        ${ring(s.total ? s.done / s.total : 0, { size: 52, stroke: 3 })}
        <span class="stop__num">${s.complete ? icon('check', { size: 20, stroke: 2.5 }) : ch}</span>
      </span>
      <span class="stop__body">
        <span class="stop__sa iast" lang="sa-Latn">${meta.iast}</span>
        <span class="stop__title"><span class="sr-only">Chapter ${ch}: </span>${meta.title}</span>
        <span class="stop__meta">${plural(meta.lessons.length, 'lesson')} · ${meta.verses} verses</span>
      </span>
      <span class="stop__status">${status(s, isNext)}</span>
    </a>
  </li>`;
}

export default async function journey() {
  const idx = getIndex();
  const progress = getProgress();
  const stats = overallStats(progress);
  const next = nextStop(progress);
  const nextCh = next ? next.lesson.ch : -1;
  const pct = percent(stats.done, stats.total);
  const bgDone = chapterStats(progress, 0).done;

  return {
    title: 'Journey',
    html: html`<div class="journey">
      ${topbar({ title: 'Journey' })}
      <header class="wrap page-head journey__head">
        <p class="eyebrow">Your journey</p>
        <h1 class="h1">From night to morning</h1>
        <p class="lede">The Gita opens in darkness, with a warrior frozen by despair, and ends in clear morning light. Eighteen chapters, in three movements, each read a few verses at a time in short lessons.</p>
        <div class="journey__summary card card--pad">
          ${ring(stats.fraction, { size: 64, stroke: 5, label: `${pct}%`, title: `${pct} percent complete` })}
          <p class="journey__summary-text"><strong>${stats.done} of ${stats.total} lessons</strong><span class="muted small">${stats.verses} of 700 verses read · ${stats.chaptersDone} of 18 chapters complete</span></p>
        </div>
      </header>
      <div class="wrap">
        <ul class="list journey__extras">
          <li><a class="list__item" href="/guide"><span class="list__lead" aria-hidden="true">${icon('guide', { size: 20 })}</span><span class="list__body"><span class="list__title">How it works</span><span class="list__sub">Chapters, lessons and verses, and how to find your way</span></span><span class="list__trail">${icon('chevron-right', { size: 18 })}</span></a></li>
          <li><a class="list__item" href="/chapter/0"><span class="list__lead" aria-hidden="true">${icon('sunrise', { size: 20 })}</span><span class="list__body"><span class="list__title">Background <span class="badge">Optional</span></span><span class="list__sub">What the Gita is, the story so far and who's who · ${bgDone ? `${bgDone} of 4 read` : '4 short lessons'}</span></span><span class="list__trail">${icon('chevron-right', { size: 18 })}</span></a></li>
        </ul>
        ${idx.parts.map((part) => html`<section class="part" aria-labelledby="part-${part.id}-title" id="part-${part.id}" style="--part-a:${chapterSky(part.chapters[0]).top};--part-b:${chapterSky(part.chapters[5]).bottom}">
          <header class="part__head">
            <p class="eyebrow">Part ${roman(part.id)} · Chapters ${part.chapters[0]} to ${part.chapters[part.chapters.length - 1]}</p>
            <h2 class="part__title" id="part-${part.id}-title"><span>${part.title}</span> <span class="part__sa deva" lang="sa">${part.sa}</span></h2>
            <p class="part__blurb">${part.blurb}</p>
          </header>
          <ol class="path">
            ${part.chapters.map((ch) => stop(ch, idx.chapters[ch - 1], progress, nextCh))}
          </ol>
        </section>`)}
      </div>
    </div>`,
  };
}
